import { Line } from '@react-three/drei'
import { useMemo } from 'react'
import * as THREE from 'three'
import {
  comprimento,
  multiplicar,
  normalizar,
  paraArray,
  somar,
  subtrair,
  type Vetor3,
} from '../../dominio/geometria/vetor3'

interface Props {
  origem: Vetor3
  destino: Vetor3
  cor: string
  espessura?: number
  tracejado?: boolean
  /** desenha a ponta de seta no destino */
  comPonta?: boolean
}

/** Vetor didático: haste em linha e ponta em cone, orientada pelo próprio vetor. */
export function Vetor3D({ origem, destino, cor, espessura = 4, tracejado = false, comPonta = true }: Props) {
  const delta = subtrair(destino, origem)
  const tamanho = comprimento(delta)

  // o cone do Three.js aponta para +Y; alinhar +Y à direção do vetor
  const orientacaoPonta = useMemo<[number, number, number, number]>(() => {
    if (tamanho < 1e-6) return [0, 0, 0, 1]
    const d = normalizar(delta)
    const q = new THREE.Quaternion().setFromUnitVectors(
      new THREE.Vector3(0, 1, 0),
      new THREE.Vector3(d.x, d.y, d.z),
    )
    return [q.x, q.y, q.z, q.w]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delta.x, delta.y, delta.z, tamanho])

  if (tamanho < 1e-6) return null

  const alturaPonta = Math.min(0.22, tamanho * 0.3)
  const fimHaste = comPonta
    ? somar(origem, multiplicar(normalizar(delta), tamanho - alturaPonta))
    : destino

  return (
    <group>
      <Line
        points={[paraArray(origem), paraArray(fimHaste)]}
        color={cor}
        lineWidth={espessura}
        dashed={tracejado}
        dashSize={0.12}
        gapSize={0.08}
      />
      {comPonta && (
        <mesh position={paraArray(somar(fimHaste, multiplicar(normalizar(delta), alturaPonta / 2)))} quaternion={orientacaoPonta}>
          <coneGeometry args={[alturaPonta * 0.42, alturaPonta, 14]} />
          <meshStandardMaterial color={cor} emissive={cor} emissiveIntensity={0.4} />
        </mesh>
      )}
    </group>
  )
}
